import { gql } from '@apollo/client';

/* ── portfolio statement import ─────────────────────────────── */

/**
 * The preview shape, shared by the upload mutation and the re-read query so a
 * reload cannot show a different screen from the one the upload produced.
 *
 * Plain template consts rather than gql fragments because
 * `scripts/validate-graphql.js` resolves only `${NAME}` interpolations.
 *
 * `totals` and the two JSON fields on the apply response are JSON scalars —
 * they take no subselection and arrive as plain objects.
 */
export const STATEMENT_UPLOAD_FIELDS = `
  id
  status
  error
  sourceFormat
  issuer
  casType
  fileName
  fileSize
  statementFrom
  statementTo
  investorName
  investorPanMasked
  createdAt
  targetPortfolioSymbol
  appliedAt
`;

/**
 * `investor` is the statement's own holder, resolved against the platform: who
 * this statement belongs to, whether they are already a user, and how they were
 * matched. It is what decides whose portfolio the import becomes — the client
 * is identified from the document rather than chosen on screen.
 *
 * `advisor` is the ARN the folio sits under, carried on both schemes and
 * holdings — it is what the book split is drawn from, and the one fact a CAS
 * has that no other screen in the app can see.
 *
 * `masterSchemeName` is what the security master calls the ISIN the statement
 * printed. When it disagrees with `schemeName` the match is suspect, which is
 * why both travel together rather than the screen showing only one.
 */
export const STATEMENT_PREVIEW_FIELDS = `
  upload {
    ${STATEMENT_UPLOAD_FIELDS}
  }
  investor {
    status
    userId
    matchedBy
    name
    panMasked
    email
    phone
    message
  }
  targetPortfolioSymbol
  targetExists
  portfolioName
  startDate
  totals
  issues {
    code
    severity
    message
    schemeKey
  }
  schemes {
    schemeKey
    folio
    amc
    schemeName
    isin
    rta
    rtaCode
    advisor
    status
    matchedVia
    securitySymbol
    masterSchemeName
    warnings
    candidates
  }
  holdings {
    schemeKey
    folio
    amc
    schemeName
    isin
    advisor
    securitySymbol
    matchStatus
    units
    nav
    navDate
    costValue
    marketValue
    existingUnits
    diffStatus
  }
  transactions {
    schemeKey
    securitySymbol
    transactionId
    date
    description
    canonicalType
    side
    quantity
    price
    amount
    fees
    diffStatus
  }
`;

/**
 * A parsed statement, recomputed against the reader's decisions.
 *
 * This is not only a re-read: `overrides` and `goalType` are re-applied server
 * side, so the preview that comes back is the SERVER's verdict on what those
 * decisions mean — an overridden scheme returns `MATCHED` with
 * `matchedVia: OVERRIDE` and the master's own name for the fund that was
 * picked. That is why the screen asks again after every choice rather than
 * predicting the answer: a local guess can show a confident match the backend
 * would refuse.
 *
 * `overrides` is the COMPLETE set, not a patch. Sending one override drops
 * every other, so the caller resends all of them each time.
 *
 * `goalType` names the goal the portfolio is created under and decides what it
 * is called; `CapitalTarget` when nothing is sent. An unknown one is refused.
 *
 * `startDate` (YYYY-MM-DD) is the day every fund on a holdings sheet is booked
 * as bought, and so the portfolio's start date. Accepted ONLY when
 * `upload.sourceFormat` is `SHEET` — any other format is refused if it is sent.
 * The preview echoes the date in effect as `preview.startDate`.
 *
 * Scoped to the caller, so an advisor reading a client's import sends the
 * client through `asClient` (X-Target-User-Id) exactly as the upload did.
 */
export const GET_STATEMENT_PREVIEW = gql`
  query GetPortfolioStatementPreview(
    $uploadId: Int!
    $overrides: [SchemeMatchOverrideInput!]
    $goalType: String
    $startDate: Date
  ) {
    getPortfolioStatementPreview(
      uploadId: $uploadId
      overrides: $overrides
      goalType: $goalType
      startDate: $startDate
    ) {
      success
      message
      preview {
        ${STATEMENT_PREVIEW_FIELDS}
      }
    }
  }
`;

/**
 * The goals a portfolio can be created under.
 *
 * Nine active kinds, each with the name the API takes (`name`, which is the
 * `goalType` argument) and the words a person should see (`displayName`). The
 * enum behind it carries thirteen, but four are not offered — this list is the
 * one the desk chooses from.
 *
 * Not `getInvestingGoalTypeEnum`, which is a different enum entirely: it
 * answers trading vs investing, not what the money is for.
 */
export const GET_SUPPORTED_GOALS = gql`
  query GetSupportedGoals($limit: Int, $language: String) {
    getSupportedGoals(limit: $limit, language: $language) {
      investmentgoalId
      name
      displayName
      description
      measurement
      isActive
    }
  }
`;

/**
 * Statements uploaded for this client, newest first — the import history.
 *
 * `appliedAt` is what separates a statement that was only looked at from one
 * that became a portfolio; `targetPortfolioSymbol` names what it became.
 */
export const LIST_STATEMENT_UPLOADS = gql`
  query ListPortfolioStatementUploads($limit: Int!) {
    listPortfolioStatementUploads(limit: $limit) {
      success
      message
      uploads {
        ${STATEMENT_UPLOAD_FIELDS}
      }
    }
  }
`;
